import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { leaveServices } from '../../services/leaveServices';
import { holidayServices } from '../../services/holidayServices';
import { leaveBalanceServices } from '../../services/leaveBalanceServices';
import { notificationTrackerService } from '../../services/notificationTrackerService';
import { useSnackbar } from '../../utils/useSnackbar';
import PageHeader from '../ui/PageHeader';
import HolidayDatePicker from '../common/HolidayDatePicker';
import '../../css/Leave.css';
import '../../css/Dialog.css';
import { LEAVE_TYPE, LEAVE_TYPE_LABELS, isHalfDay } from '../../constants/leaveType';

const toLocalDateTime = (dateStr) => {
  if (!dateStr) return null;
  return `${dateStr}T00:00:00`;
};

/** Convert yyyy-MM-dd → dd/MM/yyyy for the notification API */
const toDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  return dateStr.split('-').reverse().join('/');
};

// ─── Date boundary helpers ────────────────────────────────────────────────────
const toDateStr = (d) => {
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMinStartDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return toDateStr(d);
};

const getMaxEndDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return toDateStr(d);
};

// ─── Leave day calculation ────────────────────────────────────────────────────
/**
 * Count leave days between startStr and endStr (inclusive).
 * Half-day leaves: always 0.5, no sandwich rule.
 * Full-day sandwich rule: if notice period (today → startDate) < 30 days,
 * weekends are included. Otherwise weekends are NOT counted.
 * Returns { days, isSandwich }
 */
const calcLeave = (startStr, endStr, leaveType = 'FULL_DAY', holidays = []) => {
  if (!startStr) return null;

  const holidaySet = new Set(
    (holidays || []).map((h) => h?.holidayDate?.split('T')[0]).filter(Boolean)
  );

  // Half-day: check if on holiday
  if (isHalfDay(leaveType)) {
    if (holidaySet.has(startStr)) {
      return { days: 0, isSandwich: false };
    }
    return { days: 0.5, isSandwich: false };
  }

  if (!endStr || endStr < startStr) return null;

  // Single day full day on holiday
  if (startStr === endStr && holidaySet.has(startStr)) {
    return { days: 0, isSandwich: false };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [sy, sm, sd] = startStr.split('-').map(Number);
  const [ey, em, ed] = endStr.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);

  // Days of notice given (negative = past leave)
  const noticeDays = Math.round((start - today) / (1000 * 60 * 60 * 24));

  // Sandwich rule kicks in when notice < 30 days AND range contains a weekend
  const sandwichRuleApplies = noticeDays < 30;

  let days = 0;
  let hasWeekend = false;
  const cur = new Date(start);
  while (cur <= end) {
    const dateISO = toDateStr(cur);
    const isHoliday = holidaySet.has(dateISO);
    const dow = cur.getDay(); // 0 = Sun, 6 = Sat
    const isWeekend = dow === 0 || dow === 6;
    if (isWeekend) hasWeekend = true;

    // Holidays are excluded from leave days deduction
    if (!isHoliday) {
      if (sandwichRuleApplies || !isWeekend) days++;
    }
    cur.setDate(cur.getDate() + 1);
  }

  // Only flag as sandwich if weekends were actually present AND counted
  const isSandwich = sandwichRuleApplies && hasWeekend && days > 0;
  return { days, isSandwich };
};

const badgeLabel = (info) => {
  if (!info || info.days === 0) return '0 days leave';
  const dayStr = info.days === 0.5 ? 'Half day' : `${info.days} day${info.days !== 1 ? 's' : ''}`;
  return info.isSandwich ? `${dayStr} leave · sandwich leave` : `${dayStr} leave`;
};

const ApplyLeave = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const navigate = useNavigate();

  const [leaveType, setLeaveType] = useState(LEAVE_TYPE.FULL_DAY);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [appliedLeaves, setAppliedLeaves] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // ── Auto-filled from Redux ─────────────────────────────────────────────
  const userId = userData?.id;
  const userName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
  const teamId = userData?.teamId ?? null;
  const userRoles = userData?.roles ?? [];

  // ── Date boundary values ───────────────────────────────────────────────
  const minStartDate = getMinStartDate();
  const maxEndDate = getMaxEndDate();

  // ── Fetch company holidays for the active date window ──────────────────
  useEffect(() => {
    holidayServices.getHolidaysBetween(minStartDate, maxEndDate)
      .then((res) => {
        setHolidays(Array.isArray(res) ? res : res?.content || []);
      })
      .catch((err) => {
        console.error('Failed to load holidays for leave picker:', err);
      });
  }, [minStartDate, maxEndDate]);

  // ── Fetch user's leave balance ────────────────────────────────────────
  useEffect(() => {
    if (userId) {
      setBalanceLoading(true);
      leaveBalanceServices.getBalanceByUserId(userId)
        .then((res) => {
          setLeaveBalance(res?.balance != null ? res.balance : 0);
        })
        .catch((err) => {
          console.error('Failed to load leave balance:', err);
        })
        .finally(() => {
          setBalanceLoading(false);
        });
    }
  }, [userId]);

  // ── Fetch user's applied leaves for calendar highlight in the window ────
  useEffect(() => {
    if (userId) {
      leaveServices.getLeavesByUserId(userId, 'ALL', minStartDate, maxEndDate, 0, 100)
        .then((res) => {
          setAppliedLeaves(res?.content || []);
        })
        .catch((err) => {
          console.error('Failed to load user leaves for leave picker:', err);
        });
    }
  }, [userId, minStartDate, maxEndDate]);

  const isSubAdmin = userRoles.includes('SUB_ADMIN');
  const isAdmin = userRoles.includes('ADMIN') || userRoles.includes('SUPER_ADMIN');
  const showMyLeaves = isSubAdmin || (userRoles.includes('MANAGE_TASKS') && !userRoles.includes('MANAGE_JOBS') && !isAdmin);
  const showLeaveHistory = isAdmin || userRoles.includes('MANAGE_JOBS');

  // ── Half-day derived state ─────────────────────────────────────────────
  const halfDay = isHalfDay(leaveType);
  // For half-day the effective end date is always the start date
  const effectiveEndDate = halfDay ? startDate : endDate;

  // ── Live leave calculation ─────────────────────────────────────────────
  const leaveInfo = useMemo(
    () => calcLeave(startDate, effectiveEndDate, leaveType, holidays),
    [startDate, effectiveEndDate, leaveType, holidays]
  );

  // ── Holidays in selected range ─────────────────────────────────────────
  const holidaysInRange = useMemo(() => {
    if (!startDate || !holidays?.length) return [];
    const end = halfDay ? startDate : (endDate || startDate);
    return holidays.filter((h) => {
      const d = h.holidayDate?.split('T')[0];
      return d && d >= startDate && d <= end;
    });
  }, [startDate, endDate, halfDay, holidays]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const fmtDisplay = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      year: 'numeric', month: 'short', day: '2-digit',
    });
  };

  // ── Leave type change ─────────────────────────────────────────────────
  const handleLeaveTypeChange = (_, newType) => {
    if (!newType) return; // prevent deselecting all
    setLeaveType(newType);
    // When switching to half-day, clear end date (it will be locked to start date)
    if (isHalfDay(newType)) setEndDate('');
    setErrors({});
  };

  // ── Validation ────────────────────────────────────────────────────────
  const validate = () => {
    if (!startDate) {
      showSnackbar('Please select a start date.', 'error');
      return false;
    }
    if (!halfDay) {
      if (!endDate) {
        showSnackbar('Please select an end date.', 'error');
        return false;
      }
      if (startDate && endDate && endDate < startDate) {
        showSnackbar('End date cannot be before start date.', 'error');
        return false;
      }
    }

    const holidayMap = {};
    holidays.forEach((h) => {
      if (h?.holidayDate) holidayMap[h.holidayDate.split('T')[0]] = h;
    });

    // 1. Single day leave on holiday
    if (startDate && (halfDay || startDate === (effectiveEndDate || startDate))) {
      if (holidayMap[startDate]) {
        showSnackbar(`Selected date is a company holiday (${holidayMap[startDate].name}). Leave cannot be applied on a holiday.`, 'error');
        return false;
      }
    } else if (leaveInfo && leaveInfo.days === 0) {
      // 2. Multi-day range with only holidays / off-days
      showSnackbar('Selected date range contains only holidays or non-working days. No leave required.', 'error');
      return false;
    }

    if (!reason.trim()) {
      showSnackbar('Please enter a reason for your leave.', 'error');
      return false;
    }
    return true;
  };

  // ── Step 1: validate then open review dialog ──────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setConfirmOpen(true);
  };

  // ── Step 2: user confirmed — call the API ─────────────────────────────
  const handleConfirmedSubmit = async () => {
    setConfirmOpen(false);

    const payload = {
      userId,
      userName,
      teamId,
      startDate: toLocalDateTime(startDate),
      endDate: toLocalDateTime(effectiveEndDate),
      reason: reason.trim(),
      leaveType,
    };

    try {
      setSubmitting(true);
      await leaveServices.applyLeave(payload);
      showSnackbar('Leave application submitted successfully!', 'success');
      setLeaveType(LEAVE_TYPE.FULL_DAY);
      setStartDate('');
      setEndDate('');
      setReason('');
      setErrors({});

      // Redirect immediately after success
      if (showMyLeaves) {
        navigate('/home/my-leaves');
      } else if (showLeaveHistory) {
        navigate('/home/leave-history');
      }

    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        'Failed to apply for leave. Please try again.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Badge label helper ─────────────────────────────────────────────────
  const badgeLabel = (info) => {
    const dayStr = info.days === 0.5 ? 'Half day' : `${info.days} day${info.days !== 1 ? 's' : ''}`;
    if (info.isSandwich) return `${dayStr} leave · sandwich leave`;
    return `${dayStr} leave`;
  };

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Apply Leave"
        left={
          <IconButton onClick={() => navigate(-1)} size="medium">
            <ArrowBackIcon />
          </IconButton>
        }
      />

      {SnackbarComponent}

      {/* ── Review & Confirm dialog ─────────────────────────────── */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        slotProps={{ paper: { className: 'modern-dialog' } }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="modern-dialog-title">
          <span className="modern-dialog-title-text">Review Leave Details</span>
          <IconButton
            aria-label="close"
            onClick={() => setConfirmOpen(false)}
            className="modern-dialog-close-btn"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className="modern-dialog-content">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please verify the details below before submitting. This action cannot be undone.
          </Typography>

          {/* Detail rows */}
          {[
            { label: 'Applicant', value: userName },
            { label: 'Leave Type', value: LEAVE_TYPE_LABELS[leaveType] },
            { label: 'Start Date', value: fmtDisplay(startDate) },
            ...(!halfDay ? [{ label: 'End Date', value: fmtDisplay(endDate) }] : []),
          ].map(({ label, value }) => (
            <Box key={label}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                  {value}
                </Typography>
              </Box>
              <Divider />
            </Box>
          ))}

          {/* Leave day count summary in dialog */}
          {leaveInfo && (
            <div className={`leave-day-badge ${leaveInfo.isSandwich ? 'leave-day-badge--sandwich' : 'leave-day-badge--normal'}`}>
              {leaveInfo.isSandwich
                ? <WarningAmberIcon className="leave-day-badge-icon" />
                : <InfoOutlinedIcon className="leave-day-badge-icon" />
              }
              <Typography variant="body2" className="leave-day-badge-text">
                {badgeLabel(leaveInfo)}
              </Typography>
            </div>
          )}

          {/* Reason block */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
              Reason
            </Typography>
            <Box
              sx={{
                bgcolor: 'rgba(4,88,100,0.05)',
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                border: '1px solid rgba(4,88,100,0.12)',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>
                {reason}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className="modern-dialog-actions">
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{ borderColor: '#e2e8f0', color: '#64748b' }}
          >
            Back to Edit
          </Button>
          <Button
            id="leave-confirm-submit-btn"
            onClick={handleConfirmedSubmit}
            variant="contained"
            disableElevation
            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
            sx={{
              bgcolor: 'var(--primary)',
              '&:hover': { bgcolor: 'var(--primary)', opacity: 0.88 },
            }}
          >
            Confirm & Submit
          </Button>
        </DialogActions>
      </Dialog>

      <div className="leave-form-wrap">
        <Paper elevation={0} className="leave-form-card">
          {/* Card header strip */}
          <div className="leave-card-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <EventNoteIcon sx={{ color: '#fff', fontSize: 20 }} />
              <Typography className="leave-card-header-title" variant="subtitle1">
                Leave Application Form
              </Typography>
            </Box>
            {userName && (
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                Applying as <strong style={{ color: '#ffffff' }}>{userName}</strong>
              </Typography>
            )}
          </div>

          {/* Compact Leave Balance & History Card */}
          <Box
            sx={{
              mx: { xs: 2, sm: 2.5 },
              mt: 2,
              mb: 0.5,
              py: 1,
              px: 1.75,
              borderRadius: 1,
              background: 'linear-gradient(135deg, rgba(4, 88, 100, 0.05) 0%, rgba(2, 132, 199, 0.07) 100%)',
              border: '1px solid rgba(4, 88, 100, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  backgroundColor: 'rgba(4, 88, 100, 0.12)',
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.825rem' }}>
                  Leave Balance:
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                  {balanceLoading ? (
                    <CircularProgress size={14} sx={{ color: 'var(--primary)', verticalAlign: 'middle' }} />
                  ) : (
                    `${leaveBalance ?? 0} ${leaveBalance === 1 ? 'day' : 'days'}`
                  )}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/home/leave-balance-audit', { state: { userId, userName } })}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.78rem',
                color: 'var(--primary)',
                borderColor: 'rgba(4, 88, 100, 0.3)',
                backgroundColor: '#ffffff',
                px: 1.25,
                py: 0.4,
                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                '&:hover': {
                  borderColor: 'var(--primary)',
                  backgroundColor: 'rgba(4, 88, 100, 0.04)',
                },
              }}
            >
              View History
            </Button>
          </Box>

          {/* Form body */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            className="leave-form-body"
          >
            {/* ── Leave Type toggle + live day badge ─────────────── */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75 }}>
                Leave Type
              </Typography>
              <Box className="leave-type-row">
                <ToggleButtonGroup
                  value={leaveType}
                  exclusive
                  onChange={handleLeaveTypeChange}
                  size="small"
                  sx={{
                    boxShadow: '0 0 0 1px rgba(4, 88, 100, 0.28)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRight: '1px solid rgba(4, 88, 100, 0.15)',
                      borderRadius: '0 !important',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      flex: 1,
                      minWidth: 96,
                      gap: '4px',
                      color: '#64748b',
                      padding: '5px 0',
                      transition: 'background-color 0.15s, color 0.15s',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'var(--primary)',
                        color: '#fff',
                        borderRight: '1px solid rgba(4, 88, 100, 0.25)',
                        '&:last-child': { borderRight: 'none' },
                        '&:hover': { backgroundColor: 'var(--primary)', opacity: 0.9 },
                      },
                    },
                  }}
                >
                  <ToggleButton value={LEAVE_TYPE.FULL_DAY}>
                    Full Day
                  </ToggleButton>
                  <ToggleButton value={LEAVE_TYPE.FIRST_HALF}>
                    <WbSunnyOutlinedIcon className="leave-type-btn-icon" />
                    First Half
                  </ToggleButton>
                  <ToggleButton value={LEAVE_TYPE.SECOND_HALF}>
                    <WbSunnyOutlinedIcon className="leave-type-btn-icon leave-type-btn-icon--flip" />
                    Second Half
                  </ToggleButton>
                </ToggleButtonGroup>

                {leaveInfo && leaveInfo.days > 0 && (
                  <div className="leave-day-badge-row">
                    <Chip
                      size="small"
                      icon={
                        leaveInfo.isSandwich
                          ? <WarningAmberIcon className="leave-chip-icon" />
                          : <InfoOutlinedIcon className="leave-chip-icon" />
                      }
                      label={badgeLabel(leaveInfo)}
                      className={`leave-day-chip ${leaveInfo.isSandwich ? 'leave-day-chip--sandwich' : 'leave-day-chip--normal'}`}
                    />
                  </div>
                )}
              </Box>
            </Box>


            {/* Date row */}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <HolidayDatePicker
                id="leave-start-date"
                label="Start Date"
                value={startDate}
                onChange={(val) => {
                  setStartDate(val);
                  // For full-day, reset end if it's now before start
                  if (!halfDay && endDate && val > endDate) setEndDate('');
                }}
                min={minStartDate}
                max={maxEndDate}
                holidays={holidays}
                appliedLeaves={appliedLeaves}
                required
              />
              <HolidayDatePicker
                id="leave-end-date"
                label="End Date"
                value={effectiveEndDate}
                onChange={(val) => {
                  setEndDate(val);
                }}
                min={startDate || minStartDate}
                max={maxEndDate}
                holidays={holidays}
                appliedLeaves={appliedLeaves}
                required={!halfDay}
                disabled={halfDay}
              />
            </Box>

            {/* Holiday notice if selected range falls on company holidays */}
            {holidaysInRange.length > 0 && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <CelebrationRoundedIcon sx={{ color: '#059669', fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 600 }}>
                  Includes Holiday:&nbsp;
                  {holidaysInRange.map((h) => `${toDDMMYYYY(h.holidayDate)} (${h.name})`).join(', ')}
                </Typography>
              </Box>
            )}

            {/* Leave balance exceed warning (soft info, does not block application) */}
            {leaveInfo && leaveInfo.days > 0 && leaveBalance !== null && !balanceLoading && leaveInfo.days > leaveBalance && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: '10px',
                  backgroundColor: 'rgba(245, 158, 11, 0.08)',
                  border: '1px solid rgba(245, 158, 11, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <WarningAmberIcon sx={{ color: '#d97706', fontSize: 20, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>
                  Note: This leave request ({leaveInfo.days} {leaveInfo.days === 1 ? 'day' : 'days'}) exceeds your available balance ({leaveBalance} {leaveBalance === 1 ? 'day' : 'days'}).
                </Typography>
              </Box>
            )}


            {/* Reason */}
            <TextField
              id="leave-reason"
              label="Reason"
              multiline
              rows={4}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              fullWidth
              required
              placeholder="Describe the reason for your leave..."
              size="small"
            />

            {/* Submit */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                id="leave-submit-btn"
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SendIcon fontSize="small" />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  backgroundColor: 'var(--primary)',
                  '&:hover': { backgroundColor: 'var(--primary)', opacity: 0.88 },
                }}
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </div>
    </div>
  );
};

export default ApplyLeave;
